/**
 * <license header>
 */

import { ShippingCarrierMethodData, ShippingCarrierMethodAdditionalData } from './types';

/**
 * Builder class for constructing a shipping carrier method
 */
class ShippingCarrierMethod {
  private methodData: Partial<ShippingCarrierMethodData>;

  constructor(carrierCode: string, method: string) {
    this.methodData = { carrier_code: carrierCode, method, additional_data: [] };
  }

  /**
   * Sets the display name for the shipping method
   *
   * @param methodTitle - Display name for the shipping method
   * @returns The rate builder instance for method chaining
   */
  setMethodTitle(methodTitle: string): this {
    this.methodData.method_title = methodTitle;
    return this;
  }

  /**
   * Sets the price charged to the customer
   *
   * @param price - Price charged to the customer
   * @returns The rate builder instance for method chaining
   */
  setPrice(price: number): this {
    this.methodData.price = price;
    return this;
  }

  /**
   * Sets the cost to the merchant
   *
   * @param cost - Cost to the merchant
   * @returns The rate builder instance for method chaining
   */
  setCost(cost: number): this {
    this.methodData.cost = cost;
    return this;
  }

  /**
   * Adds additional data to the shipping method
   *
   * @param key - Key for the additional data
   * @param value - Value for the additional data
   * @returns The rate builder instance for method chaining
   *
   * @example
   * ```typescript
   * rate.addAdditionalData('delivery_time', '3-5 business days')
   *     .addAdditionalData('tracking_available', true);
   * ```
   */
  addAdditionalData(key: string, value: any): this {
    const additionalDataItem: ShippingCarrierMethodAdditionalData = { key, value };
    this.methodData.additional_data?.push(additionalDataItem);
    return this;
  }

  /**
   * Gets and returns the shipping carrier method data
   *
   * @returns The shipping carrier method data
   */
  getData(): ShippingCarrierMethodData {
    return this.methodData as ShippingCarrierMethodData;
  }
}

export default ShippingCarrierMethod;
